import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { StarField } from '../components/Brand';
import { Logo3D } from '../components/Logo3D';
import { LoginInstallBanner } from '../components/LoginInstallBanner';
import { useUserStore, roleHome } from '../store/userStore';
import { loginByEmail } from '../lib/api';

const TESTERS = [
  { key: 'ellie', name: 'Ellie Hoyland' },
  { key: 'lucy', name: 'Dr Lucy France' },
  { key: 'julie', name: 'Dr Julie Wooton' },
  { key: 'charles', name: 'Charles Armstrong' },
];

const STAFF_ROLES = [
  { key: 'clinician', label: 'Clinician', icon: 'fa-user-doctor', domain: 'auralis.health', testers: ['ellie', 'lucy', 'julie'] },
  { key: 'admin', label: 'Admin', icon: 'fa-user-shield', domain: 'admin.auralis.health', testers: ['ellie', 'lucy', 'julie', 'charles'] },
  { key: 'superadmin', label: 'Super admin', icon: 'fa-crown', domain: 'superadmin.auralis.health', testers: ['ellie', 'lucy', 'julie', 'charles'] },
];

const DEMO_PATIENT_EMAIL = 'demo@auralis.health';

export default function Login() {
  const navigate = useNavigate();
  const setUser = useUserStore((s) => s.setUser);
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [focused, setFocused] = useState(false);
  const [staffRole, setStaffRole] = useState(null);

  const submit = async (e, forceEmail) => {
    if (e) e.preventDefault();
    const value = (forceEmail || email).trim().toLowerCase();
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) {
      toast.error('Please enter a valid email address');
      return;
    }
    setLoading(true);
    try {
      const data = await loginByEmail(value);
      if (data.success && data.user) {
        setUser(data.user);
        toast.success(`Welcome back, ${data.user.name.split(' ')[0]}`);
        navigate(roleHome(data.user.role || 'patient'), { replace: true });
      } else {
        toast.message('Account not found', {
          description: 'Let\u2019s create a new account with this email.',
          action: { label: 'Create', onClick: () => navigate(`/onboarding?email=${encodeURIComponent(value)}`) },
        });
      }
    } catch (err) {
      toast.error('Unable to connect. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen aura-space relative flex items-center justify-center px-4 py-10 overflow-hidden">
      <StarField count={90} />
      <LoginInstallBanner />
      {/* animated aurora orbs */}
      <div className="aura-orb animate-aurora" style={{ width: 340, height: 340, top: '-6%', left: '-8%', background: 'rgba(168,85,247,0.4)' }} />
      <div className="aura-orb animate-aurora" style={{ width: 260, height: 260, bottom: '2%', right: '-6%', background: 'rgba(236,72,153,0.32)', animationDelay: '3s' }} />
      <div className="aura-orb animate-aurora" style={{ width: 200, height: 200, top: '40%', left: '55%', background: 'rgba(45,212,191,0.22)', animationDelay: '6s' }} />
      {/* subtle grid glow */}
      <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'linear-gradient(rgba(255,255,255,0.6) 1px, transparent 1px), linear-gradient(90deg, rgba(255,255,255,0.6) 1px, transparent 1px)', backgroundSize: '64px 64px', maskImage: 'radial-gradient(ellipse at center, black, transparent 75%)' }} />

      <div className="relative w-full max-w-md animate-fade-up">
        {/* Brand */}
        <div className="flex flex-col items-center mb-6">
          <Logo3D size={104} />
          <div className="flex items-baseline gap-2 mt-2 animate-fade-scale" style={{ animationDelay: '0.15s' }}>
            <span className="font-display text-5xl text-white font-bold tracking-tight" style={{ textShadow: '0 6px 30px rgba(168,85,247,0.55)' }}>Auralis</span>
            <span className="font-display text-5xl aura-gradient-text font-light">Health</span>
          </div>
          <div className="text-[11px] tracking-[0.3em] uppercase text-white/60 mt-3 animate-fade-scale" style={{ animationDelay: '0.25s' }}>
            Precision Women&apos;s Health
          </div>
        </div>

        {/* Form card */}
        <div className="glass-dark rounded-3xl p-6 sm:p-7 animate-fade-scale" style={{ animationDelay: '0.3s', boxShadow: '0 30px 60px -20px rgba(11,11,31,0.7)' }}>
          <h2 className="font-display text-2xl text-white mb-1">Welcome</h2>
          <p className="text-white/60 text-sm mb-6">Sign in to continue your health journey</p>

          <form onSubmit={submit}>
            <div
              className={`flex items-center gap-3 rounded-2xl px-4 mb-4 border transition-base ${
                focused ? 'border-[var(--aura-purple-light)] bg-[rgba(168,85,247,0.1)]' : 'border-white/10 bg-white/5'
              }`}
            >
              <i className={`fa-solid fa-envelope ${focused ? 'text-[var(--aura-purple-light)]' : 'text-white/40'}`} />
              <input
                data-testid="login-email-input"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onFocus={() => setFocused(true)}
                onBlur={() => setFocused(false)}
                placeholder="Enter your email"
                autoComplete="email"
                className="flex-1 bg-transparent py-4 text-white placeholder-white/40 outline-none"
              />
            </div>

            <button
              data-testid="login-submit-btn"
              type="submit"
              disabled={loading}
              className="w-full aura-btn-gradient text-white font-semibold rounded-2xl py-4 flex items-center justify-center gap-2 disabled:opacity-70 transition-base"
            >
              {loading ? (
                <i className="fa-solid fa-circle-notch fa-spin" />
              ) : (
                <>
                  Sign In <i className="fa-solid fa-arrow-right" />
                </>
              )}
            </button>
          </form>

          <div className="flex items-center gap-3 my-5">
            <div className="h-px flex-1 bg-white/10" />
            <span className="text-white/40 text-xs">or</span>
            <div className="h-px flex-1 bg-white/10" />
          </div>

          <button
            data-testid="create-account-btn"
            onClick={() => navigate('/onboarding')}
            className="w-full rounded-2xl py-3.5 border border-[rgba(168,85,247,0.4)] bg-[rgba(168,85,247,0.08)] text-[var(--aura-purple-light)] font-semibold flex items-center justify-center gap-2 transition-base hover:bg-[rgba(168,85,247,0.16)]"
          >
            <i className="fa-solid fa-circle-plus" /> Create New Account
          </button>
        </div>

        {/* Quick demo access */}
        <div className="mt-5 glass-dark rounded-2xl p-4" data-testid="quick-demo-access">
          <div className="text-white/50 text-[11px] uppercase tracking-wider mb-2">Quick demo access</div>
          {!staffRole ? (
            <div className="flex flex-wrap gap-2">
              <button
                data-testid="demo-login-demo-patient"
                onClick={() => submit(null, DEMO_PATIENT_EMAIL)}
                className="text-xs text-white/80 rounded-full px-3 py-1.5 bg-white/5 border border-white/10 hover:bg-white/10 transition-base"
              >
                <i className="fa-solid fa-user mr-1.5 opacity-70" />Demo patient
              </button>
              {STAFF_ROLES.map((r) => (
                <button
                  key={r.key}
                  data-testid={`demo-role-${r.key}`}
                  onClick={() => setStaffRole(r)}
                  className="text-xs text-white/80 rounded-full px-3 py-1.5 bg-white/5 border border-white/10 hover:bg-white/10 transition-base"
                >
                  <i className={`fa-solid ${r.icon} mr-1.5 opacity-70`} />{r.label} <i className="fa-solid fa-chevron-right text-[9px] ml-1 opacity-60" />
                </button>
              ))}
            </div>
          ) : (
            <div data-testid="demo-tester-picker">
              <div className="flex items-center justify-between mb-2">
                <div className="text-white/80 text-sm">
                  <i className={`fa-solid ${staffRole.icon} mr-1.5 text-[var(--aura-purple-light)]`} />
                  Sign in as <span className="font-semibold">{staffRole.label}</span> — who are you?
                </div>
                <button
                  data-testid="demo-tester-back"
                  onClick={() => setStaffRole(null)}
                  className="text-xs text-white/60 hover:text-white transition-base"
                >
                  <i className="fa-solid fa-arrow-left mr-1" />Back
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {TESTERS.filter((t) => staffRole.testers.includes(t.key)).map((t) => (
                  <button
                    key={t.key}
                    data-testid={`demo-tester-${staffRole.key}-${t.key}`}
                    disabled={loading}
                    onClick={() => submit(null, `${t.key}@${staffRole.domain}`)}
                    className="text-xs text-white rounded-full px-3 py-1.5 bg-[rgba(168,85,247,0.18)] border border-[rgba(168,85,247,0.45)] hover:bg-[rgba(168,85,247,0.3)] transition-base disabled:opacity-60"
                  >
                    {t.name}
                  </button>
                ))}
              </div>
              <p className="text-white/40 text-[11px] mt-2">Your sign-ins are recorded so we can trace any issues you run into.</p>
            </div>
          )}
        </div>

        {/* Auralis site map */}
        <div className="mt-4 glass-dark rounded-2xl p-4" data-testid="site-map-section">
          <div className="flex items-center justify-between mb-1">
            <div className="text-white/50 text-[11px] uppercase tracking-wider"><i className="fa-solid fa-map mr-1.5" />Auralis site map</div>
            <button data-testid="site-map-open" onClick={() => navigate('/site-map')} className="text-[11px] text-white/60 hover:text-white transition-base">Full overview <i className="fa-solid fa-arrow-right ml-1" /></button>
          </div>
          <p className="text-white/55 text-xs mb-3">New here? Pick a role for a step-by-step walkthrough of how Auralis works.</p>
          <div className="grid grid-cols-3 gap-2">
            {[
              { k: 'patient', l: 'Patients', i: 'fa-user', c: '#EC4899' },
              { k: 'clinician', l: 'Clinicians', i: 'fa-user-doctor', c: '#A855F7' },
              { k: 'admin', l: 'Admin', i: 'fa-user-shield', c: '#14B8A6' },
            ].map((r) => (
              <button
                key={r.k}
                data-testid={`site-map-${r.k}`}
                onClick={() => navigate(`/site-map?role=${r.k}`)}
                className="rounded-xl px-3 py-3 bg-white/5 border border-white/10 hover:bg-white/10 transition-base text-left"
              >
                <div className="grid place-items-center w-8 h-8 rounded-lg mb-2 text-white text-sm" style={{ background: r.c }}><i className={`fa-solid ${r.i}`} /></div>
                <div className="text-white text-sm font-semibold">{r.l}</div>
                <div className="text-white/40 text-[11px]">Walk me through</div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex items-center justify-center gap-2 mt-6 text-emerald-300/90 text-xs">
          <i className="fa-solid fa-shield-halved" /> NHS Compatible
        </div>
        <p className="text-center text-white/40 text-xs mt-2">
          Clinical staff: use your registered email for portal access
        </p>
      </div>
    </div>
  );
}
